import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useAuth } from '@/auth/useAuth';
import { useAuthStatus } from '@/auth/authStatus';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: `linear-gradient(135deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorBrandBackgroundPressed} 100%)`,
  },
  card: {
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    padding: '48px 64px',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow28,
  },
  title: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    marginTop: '24px',
    marginBottom: '8px',
  },
  message: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
  },
});

/**
 * Auth redirect page - handles MSAL redirect response.
 * This page is shown while the AuthProvider processes the auth response from Azure AD.
 * Once authenticated, it redirects to the stored return URL or /overview.
 */
export const AuthRedirectPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { isVerifyingAuth } = useAuthStatus();

  useEffect(() => {
    // Guard against firing before AuthProvider has finished consuming the
    // MSAL redirect response. Without this, AuthRedirectPage's effect can
    // commit on the same React tick as MsalProvider/AuthProvider (effects
    // run child-first), see `(isLoading=false, isAuthenticated=false)`
    // during MSAL's Startup window, and synchronously
    // `navigate('/login', { replace: true })`. That replaceState wipes the
    // `?code=…` query param from the URL before MSAL's async
    // `handleRedirectPromise()` reads it — silently breaking sign-in and
    // sending the user into a /login → AAD → /login loop. `isLoading` now
    // also covers `InteractionStatus.Startup`, but the explicit
    // `isVerifyingAuth` check is the primary guard.
    if (isVerifyingAuth || isLoading) return;

    // Once authentication is complete (not loading anymore), redirect
    const returnUrl = sessionStorage.getItem('authReturnUrl') || '/';
    sessionStorage.removeItem('authReturnUrl');

    if (isAuthenticated) {
      console.log('[AuthRedirect] Authentication successful, redirecting to:', returnUrl);
      navigate(returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`, { replace: true });
    } else {
      // Not authenticated after redirect - something went wrong, go to login
      console.log('[AuthRedirect] Not authenticated after redirect, going to login');
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, isVerifyingAuth, navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <Spinner size="large" />
        <h1 className={styles.title}>Completing sign-in...</h1>
        <p className={styles.message}>Please wait while we authenticate you.</p>
      </div>
    </div>
  );
};
